import { useEffect, useState } from "react";
import { Card, Table, Input, Button, Tag, message } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Users, UserPlus, Wallet, UserX } from "lucide-react";
import reapi from "../../../api/reapi";

export default function AdminUsers() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const skip = (page - 1) * limit;
      const token = localStorage.getItem("meeshoadminToken");

      const res = await reapi.get(
        `/adm/users?skip=${skip}&limit=${limit}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUsers(res.data.data.users || []);
      setStats(res.data.data.stats || {});
      setTotal(res.data.data.total || 0);
    } catch (error) {
      message.error(error?.response?.data?.message || "Users load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, limit]);

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "User",
      dataIndex: "name",
      render: (name) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">
            {name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <b>{name || "User"}</b>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      render: (v) => v || "—",
    },
    {
      title: "Credits",
      dataIndex: "wallet",
      render: (v) => <b>{v || 0}</b>,
    },
    {
      title: "Total Optimizations",
      dataIndex: "totalOptimizations",
      render: (v) => v || 0,
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      render: (date) =>
        new Date(date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) =>
        status === false ? (
          <Tag color="error">Inactive</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        ),
    },
    {
      title: "Actions",
      render: () => <Button>View</Button>,
    },
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
        <div>
          <h1 className="text-4xl font-extrabold m-0">Users</h1>
          <p className="text-slate-500 mt-2">
            Manage all registered users and their credits.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <Input
          size="large"
          placeholder="Search by name or email..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setPage(1);
            fetchUsers();
          }}
          className="rounded-xl"
        />

        <Button size="large" icon={<FilterOutlined />} className="h-12 px-8">
          Filter
        </Button>

        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          className="h-12 px-8 bg-purple-600"
        >
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <StatCard
          icon={<Users />}
          title="Total Users"
          value={stats.totalUsers || 0}
          color="purple"
        />

        <StatCard
          icon={<UserPlus />}
          title="New Users"
          value={stats.newUsers || 0}
          color="purple"
        />

        <StatCard
          icon={<Wallet />}
          title="Active Users"
          value={stats.activeUsers || 0}
          color="purple"
        />

        <StatCard
          icon={<UserX />}
          title="Inactive Users"
          value={stats.inactiveUsers || 0}
          color="purple"
          danger
        />
      </div>

      <Card className="rounded-2xl shadow-sm overflow-hidden">
        <div className="w-full overflow-x-auto">
          <Table
            loading={loading}
            columns={columns}
            dataSource={users}
            rowKey="_id"
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setLimit(ps);
              },
            }}
            scroll={{ x: 1200 }}
          />
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon, title, value, color, danger }) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-5">
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center ${
            danger
              ? "bg-red-100 text-red-600"
              : "bg-purple-100 text-purple-600"
          }`}
        >
          {icon}
        </div>

        <div>
          <p className="text-slate-500 m-0">{title}</p>
          <h2 className="text-3xl font-extrabold m-0 mt-2">{value}</h2>
          <p className={danger ? "text-red-500 m-0 mt-2" : "text-green-600 m-0 mt-2"}>
            {danger ? "↓ 4.3%" : "↑ 12.5%"}
          </p>
        </div>
      </div>
    </Card>
  );
}